import axios from "axios";
import { server } from "../config";

// 水情機具 API（controller/water.py）。
//
// 與舊的 WaterMonitor.js 的差異：
//   * 回傳具名欄位的物件，不再是位置陣列（舊版 data[27] 那種取法只要欄位
//     順序一變就整組錯位）
//   * 時間是帶時區的 ISO 字串，前端直接 new Date() 即可，不必手動減 8 小時
//   * 沒有資料時回空陣列，不是 500
//
// 串流（開關、直播、觀看者）一律用 /v1/streaming/*，不在這裡重複實作。

const base = `${server}v1/water`;

// axios 預設把 4xx/5xx 當例外。這裡統一成 {ok, data, error} 讓呼叫端不必
// 到處寫 try/catch —— 水情頁面有很多「查不到資料是正常情況」的分支。
async function call(promise) {
  try {
    const response = await promise;
    return { ok: true, data: response.data };
  } catch (error) {
    const payload = error.response?.data;
    return {
      ok: false,
      status: error.response?.status,
      error: payload?.error || error.message || "請求失敗",
      details: payload?.details,
      data: payload,
    };
  }
}

const Water = {
  // -- 測站 --------------------------------------------------------
  listStations: () => call(axios.get(`${base}/stations`)),

  getStation: (deviceId) => call(axios.get(`${base}/station/${deviceId}`)),

  updateStation: (deviceId, fields) =>
    call(axios.post(`${base}/station/${deviceId}`, fields)),

  // -- 計算設定 ----------------------------------------------------
  getConfig: (deviceId) => call(axios.get(`${base}/config/${deviceId}`)),

  // fields 可包含 params 以及 enabled / interval_min / offset_min /
  // capture_seconds / retention_days。params 有變動時後端會留一版歷史。
  updateConfig: (deviceId, fields) =>
    call(axios.post(`${base}/config/${deviceId}`, fields)),

  getConfigHistory: (deviceId, limit = 20) =>
    call(axios.get(`${base}/config/${deviceId}/history`, { params: { limit } })),

  // -- 量測資料 ----------------------------------------------------
  // start / end 可給 'YYYY-MM-DD HH:mm:ss' 或 ISO 字串
  getData: (deviceId, { start, end, limit = 500 } = {}) =>
    call(axios.get(`${base}/data/${deviceId}`, { params: { start, end, limit } })),

  getLatest: (deviceId) => call(axios.get(`${base}/data/${deviceId}/latest`)),

  // -- 工作 --------------------------------------------------------
  listJobs: (deviceId, { type, limit = 50 } = {}) =>
    call(axios.get(`${base}/jobs/${deviceId}`, { params: { type, limit } })),

  getJob: (jobId) => call(axios.get(`${base}/job/${jobId}`)),

  // type: 'manual' | 'preview' | 'reference'
  // preview 必須帶 params（未儲存的草稿）
  createJob: (deviceId, type, params) =>
    call(axios.post(`${base}/job`, { deviceID: deviceId, type, params })),

  // -- 警報（controller/water_alert.py） ----------------------------
  // 門檻欄位：water_level_{one,two,three}_height、velocity_level_*、
  // discharge_level_*（null = 該級不判斷）；發報規則：alert_enabled、
  // alert_cooldown_min、alert_weekdays、alert_time_start/end、send_to_buffer
  getAlert: (deviceId) => call(axios.get(`${base}/alert/${deviceId}`)),

  updateAlert: (deviceId, fields) =>
    call(axios.post(`${base}/alert/${deviceId}`, fields)),

  // 清除上次通知的狀態，下一筆達警戒的結果會被視為「新觸發」
  resetAlertState: (deviceId) =>
    call(axios.post(`${base}/alert/${deviceId}/reset`)),

  getAlertLog: (deviceId, limit = 20) =>
    call(axios.get(`${base}/alert/${deviceId}/log`, { params: { limit } })),

  // -- 測站照片 ----------------------------------------------------
  imageUrl: (deviceId, bust) =>
    `${base}/image/${deviceId}${bust ? `?b=${bust}` : ""}`,

  uploadImageUrl: (deviceId) => `${base}/image/${deviceId}`,
};

// 工作做完（或失敗）之前反覆查詢。手動計算要十幾秒到一分鐘，後端不會卡著
// 等，所以由前端輪詢。
export async function waitForJob(jobId, { interval = 2000, timeout = 180000,
                                          onTick } = {}) {
  const deadline = Date.now() + timeout;
  for (;;) {
    const result = await Water.getJob(jobId);
    if (!result.ok) return result;
    const job = result.data;
    if (onTick) onTick(job);
    if (["done", "failed", "skipped"].includes(job.status)) {
      return { ok: true, data: job };
    }
    if (Date.now() > deadline) {
      return { ok: false, error: "等待計算結果逾時", data: job };
    }
    await new Promise((resolve) => setTimeout(resolve, interval));
  }
}

export default Water;